import React, { useEffect, useMemo, useRef } from 'react';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import classNames from 'classnames';
import { CompetenceDto, LevelOfSkillDto, UserDto } from 'utils/wemble-api.generated';
import { useCreateSkillMutation, useGetLevelOfSkillsQuery, useUpdateSkillMutation } from 'utils/wemble-api';
import LevelScaleHeader, { MAX_NAMED_LEVEL_COLUMNS } from '../LevelScaleHeader';
import SkillLevelTrack from '../SkillLevelTrack';
import { categorySections, heldLevels } from './competenceTree';

const useStyles = makeStyles((theme) => ({
  table: {
    tableLayout: 'fixed',
    width: '100%',
    // Groups are told apart by space, not lines: rows within a group sit close
    // with no dividers, and each group opens with a wide gap above its heading,
    // so a category reads as a few blocks rather than one long list.
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      padding: '3px 0',
    },
    // Top padding matches the card's other tables (Recent Matters), so the
    // column headers sit the same distance below the section header.
    '& .MuiTableCell-head': {
      fontSize: 11,
      textTransform: 'uppercase',
      color: theme.palette.gray.g10,
      padding: '12px 0 8px',
      borderBottom: `1px solid ${theme.palette.gray.g6}`,
    },
    '& .MuiTableCell-body': {
      fontWeight: 500,
    },
  },
  trackCell: {
    paddingLeft: '2px !important',
  },
  name: {
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    paddingRight: 8,
  },
  // Competencies the person doesn't hold: clearly lighter than held ones
  // (g2 was too close to black to tell apart), still readable.
  name_missing: {
    color: '#8A8A8A',
    fontWeight: 400,
  },
  groupCell: {
    padding: '28px 0 8px !important',
  },
  groupCell_first: {
    paddingTop: '12px !important',
  },
  // Group headings must not read as one more competence: bold tracked caps in
  // the dark accent, outdented from the rows below, with a rule running to the
  // edge that marks where the group starts.
  groupLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
    color: '#38566B',
    whiteSpace: 'nowrap' as const,
    '&::after': {
      content: '""',
      flex: 1,
      height: 2,
      borderRadius: 1,
      backgroundColor: theme.palette.gray.g6,
    },
  },
  // Rows under a heading sit indented, so the heading visibly owns them.
  name_grouped: {
    paddingLeft: '14px !important',
  },
  empty: {
    fontSize: 12,
    color: theme.palette.gray.g2,
    marginTop: 12,
  },
}));

/** Every competence in the category as a row, the firm's levels as columns —
 *  the People grid on a competence, transposed onto one person. Competencies
 *  the person doesn't hold stay in the list with an empty row, so the gaps in a
 *  category are as visible as the strengths. */
const CompetenceGrid = ({
  user,
  orderUser,
  competences,
  category,
  canEdit,
}: {
  user: UserDto;
  /** Snapshot used only to order the groups (see UserCompetencesTable). */
  orderUser?: UserDto;
  competences: CompetenceDto[];
  category: string;
  canEdit: boolean;
}) => {
  const classes = useStyles();
  const { data: levelOfSkills } = useGetLevelOfSkillsQuery();
  const [updateSkill] = useUpdateSkillMutation();
  const [createSkill] = useCreateSkillMutation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('xs'));

  const levels = useMemo<LevelOfSkillDto[]>(
    () => [...(levelOfSkills ?? [])].sort((a, b) => (a.level ?? 0) - (b.level ?? 0)),
    [levelOfSkills],
  );

  // Competence id -> the person's skill, so a cell knows whether to update an
  // existing skill or create one.
  const skills = useMemo(() => {
    const map = new Map<string, string>();
    for (const skill of user.skills ?? []) {
      if (typeof skill === 'string' || !skill._id) continue;
      const competenceId = typeof skill.competence === 'string' ? skill.competence : skill.competence?._id;
      if (competenceId) map.set(competenceId, skill._id);
    }
    return map;
  }, [user.skills]);

  const held = useMemo(() => heldLevels(user), [user]);
  // Ordering uses the snapshot; what's held is always live.
  const sections = useMemo(
    () => categorySections(competences, category, orderUser ?? user),
    [competences, category, orderUser, user],
  );

  // A competence the person doesn't hold yet is created at the clicked level;
  // otherwise the existing skill is updated (null clears the level).
  // A skill created from the Grid only shows up in `user.skills` once the
  // profile refetches. Until then, further clicks on that competency wait for
  // the pending create and update the new skill, instead of creating a
  // duplicate (the API doesn't dedupe).
  const pendingCreates = useRef(new Map<string, Promise<string | undefined>>());
  useEffect(() => {
    Array.from(pendingCreates.current.keys()).forEach((id) => skills.has(id) && pendingCreates.current.delete(id));
  }, [skills]);

  const setLevel = (competence: CompetenceDto, level: LevelOfSkillDto | null) => {
    const competenceId = competence._id;
    if (!competenceId) return;
    const update = (id: string) => updateSkill({ id, skillUpdateParams: { levelOfSkillId: level?._id ?? undefined } });
    const skillId = skills.get(competenceId);
    if (skillId) {
      update(skillId);
      return;
    }
    const pending = pendingCreates.current.get(competenceId);
    if (pending) {
      pending.then((id) => {
        if (id) update(id);
      });
      return;
    }
    if (!level?._id) return;
    const created = createSkill({
      skillCreationParams: { userId: user._id ?? undefined, competenceId, levelOfSkillId: level._id },
    })
      .unwrap()
      .then((skill) => skill?._id ?? undefined)
      .catch(() => {
        pendingCreates.current.delete(competenceId);
        return undefined;
      });
    pendingCreates.current.set(competenceId, created);
  };

  if (sections.length === 0) {
    return <div className={classes.empty}>No competencies in this category.</div>;
  }

  // Once there's more than one group, every group gets a heading — including the
  // category's own direct competencies, which otherwise sat unlabelled on top.
  const categoryName = competences.find((c) => c._id === category)?.name || null;
  const labelledSections = sections.map((section) => ({
    ...section,
    label: section.label ?? (sections.length > 1 ? categoryName : null),
  }));

  return (
    <TableContainer>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: '40%' }}>Competency</TableCell>
            <LevelScaleHeader levels={levels} compact={isPhone || levels.length > MAX_NAMED_LEVEL_COLUMNS} />
          </TableRow>
        </TableHead>
        <TableBody>
          {labelledSections.map((section, index) => (
            <React.Fragment key={`${index}-${section.label ?? ''}`}>
              {section.label && (
                <TableRow>
                  <TableCell
                    className={classNames(classes.groupCell, index === 0 && classes.groupCell_first)}
                    colSpan={levels.length + 1}
                  >
                    <span className={classes.groupLabel}>{section.label}</span>
                  </TableCell>
                </TableRow>
              )}
              {section.competences.map((competence) => {
                const competenceId = competence._id || '';
                return (
                  <TableRow key={competenceId}>
                    <TableCell
                      className={classNames(
                        classes.name,
                        !held.has(competenceId) && classes.name_missing,
                        section.label && classes.name_grouped,
                      )}
                    >
                      {competence.name}
                    </TableCell>
                    <TableCell colSpan={levels.length} className={classes.trackCell}>
                      <SkillLevelTrack
                        level={held.has(competenceId) ? held.get(competenceId) : -1}
                        editable={canEdit}
                        onChange={(level) => setLevel(competence, level)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CompetenceGrid;
